import { css } from '@emotion/css'
import { Select } from '@mantine/core'
import type { TPermission } from '@semios/app-platform-value-type-definitions'
import { PanelDetailsContext } from 'App/Map/PanelDetails/PanelDetails'
import type { TValuesTimeseriesProperties } from 'App/Map/types'
import { translate } from 'i18n/i18n'
import { isEmpty } from 'lodash'
import { useContext, useMemo } from 'react'
import { colors } from 'settings/colors'
import { hubConnectorSettings } from 'settings/hubConnectorSettings'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { smallStore } from 'stores/smallStore/smallStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { checkAuthorization } from 'utils/checkAuthorization'
import { filterFieldAssetsByValueTypes } from 'utils/filterFieldAssetsByValueTypes'
import { mantineSelectSearchFilterProp } from 'utils/mantineSelectSearchFilterProp'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import { sortByKey } from 'utils/sortByKey'
import { useScreenSize } from 'utils/useScreenSize'

type SelectDataItem = { value: string; label: string }

// TODO: the types here should be more generic since we're using the value types here to check for properties' field assets having the value types as well
// Or can we do with a permission check instead but that seems like a change in functionality is more possible
export type TDropdownSelectorPropertyProps = {
  propertyPermissionsToCheck?: TPermission[]
  valuesTimeseriesToFilterOn: TValuesTimeseriesProperties[]
}

export const DropdownSelectorProperty = ({
  valuesTimeseriesToFilterOn,
  propertyPermissionsToCheck,
}: TDropdownSelectorPropertyProps) => {
  const { selectedProperty } = selectedFieldAssetsStore.useSelector((s) => ({
    selectedProperty: s.property,
  }))

  const permissions = userDetailsStore.useSelector((s) => s.permissions)
  const isPropertyPlaybackActive = smallStore.useSelector((s) => s.showPropertyPlayback)
  const { containerWidth } = useContext(PanelDetailsContext)
  const { isWideScreen } = useScreenSize()
  const finalWidth = containerWidth < 600 ? 200 : Math.min(280, Math.round(containerWidth / 3))

  let PropertyPlaybackWidth = 200

  if (!isWideScreen) {
    PropertyPlaybackWidth = 150
  }

  const { properties } = fieldAssetStore.useSelector((s) => ({
    properties: s?.properties ?? [],
  }))

  const data = useMemo<SelectDataItem[]>(() => {
    return Object.values(properties ?? {})
      .sort(sortByKey('propertyName'))
      .flatMap((p) => {
        const hasSetHeatmapPermission = checkAuthorization({
          permission: 'VIEW_TEMPERATURE_HEATMAP',
          entity: p.propertyId,
        })

        if (propertyPermissionsToCheck) {
          const hasPermission = propertyPermissionsToCheck.some(
            (permission) => permissions[permission]?.[p.propertyId],
          )

          if (!hasPermission) return []
        }

        if (!!valuesTimeseriesToFilterOn.length) {
          const propertyHasValueTypes = valuesTimeseriesToFilterOn.some((vt) =>
            p.valuesTimeseries.includes(vt),
          )

          const pointsWithValues = filterFieldAssetsByValueTypes({
            fieldAssets: Object.values(p.points || []),
            valuesTimeseries: valuesTimeseriesToFilterOn,
          })

          const blocksWithValues = filterFieldAssetsByValueTypes({
            fieldAssets: Object.values(p.blocks || []),
            valuesTimeseries: valuesTimeseriesToFilterOn,
          })

          const zonesWithValues = filterFieldAssetsByValueTypes({
            fieldAssets: Object.values(p.irrigationZoneEmitters || []),
            valuesTimeseries: valuesTimeseriesToFilterOn,
          })

          if (
            !propertyHasValueTypes &&
            isEmpty(pointsWithValues) &&
            isEmpty(blocksWithValues) &&
            isEmpty(zonesWithValues)
          )
            return []
        }

        return {
          value: String(p.propertyId),
          label: p.propertyName,
          hidden: !hasSetHeatmapPermission && isPropertyPlaybackActive,
        }
      })
  }, [selectedProperty, properties])

  if (!data.length) return null

  return (
    // can prevent the section from collapsing by passing stopPropagation
    <div onClick={(e) => e.stopPropagation()}>
      <Select
        className={css({
          width: isPropertyPlaybackActive ? PropertyPlaybackWidth : finalWidth,
          border: `1px solid ${colors.grey500}`,
          color: colors.grey800,
          borderRadius: 3,
          fontSize: isPropertyPlaybackActive && !isWideScreen ? 11 : 14,
        })}
        clearable={false}
        data={data}
        filter={mantineSelectSearchFilterProp}
        onChange={(newProperty: string) => setSelectedFieldAsset({ property: Number(newProperty) })}
        placeholder={translate.phrases.banyanApp('Select a Property')}
        searchable
        size={isPropertyPlaybackActive ? 'sm' : 'xs'}
        spellCheck="false"
        styles={{
          ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER,
          dropdown: { marginTop: -6, fontWeight: 'normal' },
          input: { ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER.input, paddingRight: 28 },
        }}
        value={selectedProperty?.toString()}
        withinPortal
        zIndex={hubConnectorSettings.zIndexForDropdownSelectorInStackemCharts}
      />
    </div>
  )
}
